// components/Chat/ZenoCapabilities.jsx
//
// Discovery panel for Zeno: users don't know what to ask because they can't
// see Zeno's tool set. This surfaces it — not as buttons that run tools (the
// concrete Create actions live in their own block), but as example questions
// grouped by outcome. Tapping one fills the composer (via onPick) so the user
// learns the phrasing and can edit before sending. "See all" reveals the full
// capability set in plain language for the curious.
import React, { useState, useRef } from "react";
import { Coins, Compass, ChevronRight, Wrench, X } from "lucide-react";

// Example questions, grouped by outcome. Deliberately skips "list my agents /
// workflows / avatars" — those already have dedicated panels; here we showcase
// the questions the dashboards can't answer (account lookups + cross-cutting
// synthesis).
const ASK_GROUPS = [
  {
    label: "Account & coins",
    Icon: Coins,
    tone: "text-amber-300",
    asks: [
      "How many coins do I have left?",
      "What's my spending this month?",
      "Give me an overview of my account",
    ],
  },
  {
    label: "Your workspace",
    Icon: Compass,
    tone: "text-[#b98bff]",
    asks: [
      "Which of my projects have no workflow yet?",
      "What can I automate with my connected apps?",
    ],
  },
];

// Full capability set in friendly language, revealed under "See all". Read-only
// — this is a reference, not a set of actions.
const ALL_CAPABILITIES = [
  {
    group: "Account & billing",
    items: [
      "Check your coin balance",
      "See your recent spending and transactions",
      "Get an overview of your account",
    ],
  },
  {
    group: "Your workspace",
    items: [
      "List or look up your projects, agents, workflows and avatars",
      "See your connected integrations",
      "Browse available models and skills",
    ],
  },
  {
    group: "Create & edit",
    items: ["Create an avatar", "Edit an avatar", "Create an agent"],
  },
];

export default function ZenoCapabilities({ onPick }) {
  const [showAll, setShowAll] = useState(false);
  const [popover, setPopover] = useState(null); // { right, bottom }
  const seeAllRef = useRef(null);

  const toggleAll = () => {
    if (showAll) {
      setShowAll(false);
      return;
    }
    const el = seeAllRef.current;
    if (el) {
      const r = el.getBoundingClientRect();
      const WIDTH = 300;
      const GAP = 12;
      // Open toward the chat: sit to the LEFT of the panel, bottom-aligned to
      // the button so it never covers the panel it came from.
      let right = window.innerWidth - r.left + GAP;
      // Fallback for narrow screens (panel is a full-width drawer, no room to
      // the left): pin to the right edge instead.
      if (window.innerWidth - right - WIDTH < 8) {
        right = Math.max(8, window.innerWidth - r.right);
      }
      setPopover({ right, bottom: window.innerHeight - r.bottom });
    }
    setShowAll(true);
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-[#1b1b1f] p-4">
      <div className="mb-1 text-sm font-semibold text-white">What you can ask Zeno</div>
      <p className="mb-3 text-xs leading-relaxed text-gray-400">
        Ask in plain language — Zeno pulls your data and acts for you.
      </p>

      <div className="space-y-3">
        {ASK_GROUPS.map((g) => {
          const Icon = g.Icon;
          return (
            <div key={g.label}>
              <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                <Icon className={`h-3.5 w-3.5 ${g.tone}`} />
                {g.label}
              </div>
              <div className="space-y-1">
                {g.asks.map((ask) => (
                  <button
                    key={ask}
                    onClick={() => onPick?.(ask)}
                    className="flex w-full items-start gap-1.5 rounded-lg px-2 py-1.5 text-left text-xs text-gray-300 transition-colors hover:bg-white/[0.05] hover:text-white"
                  >
                    <ChevronRight className="mt-0.5 h-3 w-3 flex-shrink-0 text-gray-600" />
                    <span>{ask}</span>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <button
        ref={seeAllRef}
        onClick={toggleAll}
        className="mt-3 flex w-full items-center justify-between gap-2 border-t border-white/10 pt-3 text-xs font-medium text-gray-400 transition-colors hover:text-gray-200"
      >
        See all capabilities
        <ChevronRight className={`h-3.5 w-3.5 transition-transform ${showAll ? "rotate-90" : ""}`} />
      </button>

      {showAll && popover && (
        <>
          {/* Backdrop — closes on any outside interaction */}
          <div className="fixed inset-0 z-[60]" onClick={() => setShowAll(false)} />
          <div
            className="fixed z-[70] w-[300px] max-w-[calc(100vw-1rem)]"
            style={{ right: popover.right, bottom: popover.bottom }}
          >
            <div className="max-h-[60vh] overflow-y-auto rounded-2xl border border-white/10 bg-[#1b1b1f] p-4 shadow-[0_24px_60px_rgba(0,0,0,0.6)]">
              <div className="mb-3 flex items-center gap-2">
                <Wrench className="h-4 w-4 text-[#b98bff]" />
                <span className="text-sm font-semibold text-white">Everything Zeno can do</span>
                <button
                  onClick={() => setShowAll(false)}
                  className="ml-auto rounded-full p-1 text-gray-400 transition-colors hover:bg-white/10 hover:text-white"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
              <div className="space-y-3">
                {ALL_CAPABILITIES.map((c) => (
                  <div key={c.group}>
                    <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                      {c.group}
                    </div>
                    <ul className="space-y-1">
                      {c.items.map((item) => (
                        <li key={item} className="flex items-start gap-1.5 text-xs text-gray-400">
                          <span className="mt-1.5 h-1 w-1 flex-shrink-0 rounded-full bg-[#8844F5]" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
